
import React, { useState, useEffect } from "react";
import axiosInstance from '../axiosConfig';
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import '../styles.css';

const AttendanceManagement = () => {
  const [userData, setUserData] = useState(null);
  const currentUserId = localStorage.getItem('id'); 
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then((response) => setUserData(response.data))
        .catch((error) => console.error('Error fetching user data:', error));
    }
  }, [currentUserId]);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, [currentUserId]);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
      window.location.href = '/admin-manager-lead-dashboard';
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const profilePhotoUrl = userData?.profile_photo?.startsWith('http://') 
    ? userData?.profile_photo.replace('http://', 'https://') 
    : userData?.profile_photo;

  const menuItems = [
    { icon: '🏠', label: 'Dashboard', path: '/user-dashboard' },
    { icon: '👤', label: 'My Profile', path: `/user-profile/${userData?.id}` },
    { icon: '📅', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
    { icon: '📋', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
    // { icon: '📅', label: 'Calendar', path: '/calendar', condition: userData?.role_name === 'employee' },
    { icon: '✏️', label: 'Create AR Request', path: `/ar-request/${userData?.id}` },
    { icon: '📈', label: 'My Calendar', path: '/my-calendar' },
    { icon: '📊', label: 'AR Transactions', path: '/ar-transaction/' },
    // { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/', active: false },
    { icon: '🆔', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
  ];

  const quickLinks = [
    {
      icon: '✏️',
      title: 'Create AR Request',
      description: 'Submit attendance regularization request',
      path: `/ar-request/${userData?.id}`,
      image: '/create-leave.png'
    },
    {
      icon: '📅',
      title: 'My Calendar',
      description: 'View your attendance calendar',
      path: '/my-calendar',
      image: '/leave.jpg'
    },
    {
      icon: '📋',
      title: 'AR Transactions',
      description: 'View your AR request history',
      path: '/ar-transaction/',
      image: '/apply-leave.jpg'
    }
  ];

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />

      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={userData} 
        activeRole={userData?.role_name}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout}
        menuItems={menuItems}
      />
      
      <main className="dashboard-main-content">
        <div className="attendance-management-container">

          <div className="attendance-management-header">
            <h1>Attendance Management</h1>
            <p>Manage your attendance requests and view your attendance details</p>
          </div>

          <div className="attendance-cards-grid">
            {quickLinks.map((link, index) => (
              <a key={index} href={link.path} className="attendance-card">
                <div className="attendance-card-icon">
                  <img src={link.image} alt={link.title} />
                </div>
                <div className="attendance-card-content">
                  <h3>{link.title}</h3>
                  <p>{link.description}</p>
                </div>
                <span className="attendance-card-arrow">→</span>
              </a>
            ))}
          </div>

          <div className="attendance-quick-info">
            <div className="quick-info-card">
              <div className="quick-info-header">
                <span className="info-icon">ℹ️</span>
                <h3>Quick Information</h3>
              </div>
              <div className="quick-info-content">
                <p>Use the links above to:</p>
                <ul>
                  <li>Create an attendance regularization (AR) request</li>
                  <li>View your attendance calendar</li>
                  <li>Track your AR request status</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </main>

      {/* ID Card Popup */}
      {isIdCardPopupVisible && (
        <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
          <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>×</button>
            
            <div className="id-card-header">
              <img src="/Teamhalt.png" alt="Logo" className="id-card-logo" />
              <span>TEAMHALT PRIVATE LIMITED</span>
            </div>
            
            <div className="id-card-body">
              <div className="id-card-profile">
                <img
                  src={profilePhotoUrl || '/profile.jpg'}
                  alt="Profile"
                  className="id-card-photo"
                />
                <div className="id-card-info">
                  <h4>{userData?.first_name} {userData?.last_name}</h4>
                  <p className="id-designation">{userData?.designation}</p>
                  <div className="id-dob">
                    <strong>DOB:</strong>
                    <span>
                      {userData?.date_of_birth
                        ? new Date(userData.date_of_birth).toLocaleDateString("en-GB").replace(/\//g, "-")
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
              
              <hr className="id-card-divider" />
              
              <div className="id-card-details">
                {[
                  { label: "Emp ID", value: userData?.employee_code },
                  { label: "Department", value: userData?.department },
                  { label: "ESIC No.", value: userData?.esic_no },
                  { label: "UAN No.", value: userData?.uan_no },
                  { label: "Gender", value: userData?.gender },
                  { label: "Contact No.", value: userData?.contact_number },
                ].map((item, idx) => (
                  <div key={idx} className="detail-row">
                    <strong>{item.label}:</strong>
                    <span>{item.value || "N/A"}</span>
                  </div>
                ))}
              </div>
              
              {typeof userData?.work_location_name === "string" &&
                userData.work_location_name.toLowerCase() !== "noida" && (
                <div className="id-card-duty">
                  <strong>On Contractual Duty Of:</strong>
                  <span>{userData?.duty_of || "N/A"}</span>
                </div>
              )}
              
              <div className="id-card-footer">
                https://teamhalt.com/
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AttendanceManagement;

